import {
  Directive,
  ElementRef,
  OnDestroy,
  OnInit,
  RendererStyleFlags2,
  inject,
  input,
  output,
} from '@angular/core';
import interact from 'interactjs';
import { throttle } from 'lodash-es';
import { Subscription } from 'rxjs';
import { SubSink } from 'subsink';
import { CssAttributes } from '../../features/calendar/components/time-block/rendering/css-attributes';
import { CssDropzone } from '../../features/calendar/util/calendar-style.constants';
import { AdminLayoutWrapperCSSClass } from '../constants-repository/app-style.constants';
import {
  DragActiveEntryCSSClass,
  DragHelperCSSClass,
  DragPlaceholderCSSClass,
  SortableRowCSSClass,
  SortableRowDropzoneCSSClass,
  SortableRowItemIdAttribute,
  SortedIndexAttribute,
} from '../constants-repository/sortable-rows.constants';
import { SortableEntriesService } from '../services/features/sortable-elements/sortable-entries.service';
import { ShiveRenderService } from '../services/rendering/shive-render.service';
import { TouchAndMouseHandlerService } from '../services/touch-and-mouse-handler.service';

@Directive({
  selector: '[appSortableRow]',
  standalone: true,
})
export class SortableRowsDirective<T extends { id: number }> implements OnInit, OnDestroy {
  clonedDataSource = input.required<T[]>(); // The cloned data source.
  dragHelperContent = input('');
  readonly rowDragStarted$ = output();
  readonly rowDrop$ = output<{ from: number; to: number }>();
  public readonly el = inject(ElementRef);

  private static currentDraggedRow = null; // The original row that does not move.
  private static dragHelperElement: HTMLElement | null = null; // Element close to the cursor.
  private static scrollPosY = 0;
  private static draggedSortedIndex = -1;
  private mouseHandlerSub: Subscription = null;
  private readonly onDragMoveThrottled = throttle(
    (evt: Interact.DragEvent) => this.dragMoveListener(evt),
    20,
  );
  private readonly onDragEnterThrottled = throttle(
    (evt: Interact.DropEvent) => this.onDragEnterEvent(evt),
    20,
  );

  // DI
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly touchAndMouseHandlerService = inject(TouchAndMouseHandlerService);
  private readonly sortableEntriesService = inject(SortableEntriesService);
  private readonly subs = new SubSink();

  ngOnInit() {
    this.mouseHandlerSub = this.touchAndMouseHandlerService.globalMouseReleased$.subscribe(() => {
      if (SortableRowsDirective.currentDraggedRow === this.el.nativeElement) {
        this.onDrop();
      }
    });

    this.initializeDragAndDrop();

    // Since we collapse the table row height during drag, the drop zones have to be manually re-calculated.
    interact.dynamicDrop(true);

    this.subs.sink = this.touchAndMouseHandlerService.globalMouseScroll$.subscribe((scrollPosY) => {
      SortableRowsDirective.scrollPosY = scrollPosY;

      if (!SortableRowsDirective.dragHelperElement) {
        return;
      }

      const dataY = +this.shiveRenderService.getAttribute(
        SortableRowsDirective.dragHelperElement,
        'data-y',
      );
      this.shiveRenderService.setStyle(
        SortableRowsDirective.dragHelperElement,
        CssAttributes.top,
        `${SortableRowsDirective.scrollPosY + dataY}px`,
      );
    });
  }

  ngOnDestroy() {
    interact(this.el.nativeElement).unset();
    this.mouseHandlerSub?.unsubscribe();
    this.mouseHandlerSub = null;
    this.subs.unsubscribe();
  }

  private onDragStart(event: Interact.InteractEvent): void {
    SortableRowsDirective.currentDraggedRow = this.el.nativeElement;
    this.shiveRenderService.addClass(
      SortableRowsDirective.currentDraggedRow,
      DragPlaceholderCSSClass,
    );
    this.shiveRenderService.addClass(
      SortableRowsDirective.currentDraggedRow,
      DragActiveEntryCSSClass,
    );

    SortableRowsDirective.draggedSortedIndex = +this.shiveRenderService.getAttribute(
      SortableRowsDirective.currentDraggedRow,
      SortedIndexAttribute,
    );

    // Make table content non-selectable
    this.shiveRenderService.addClass(document.body, 'noselect');

    this.createDragHelperElement();

    this.setDragHelperPosition(
      this.touchAndMouseHandlerService.currentMouseViewportPos.x,
      event.clientY,
    );

    // Append to the body so it’s not confined by parent styles.
    const mainContentEl = document.querySelector(`.${AdminLayoutWrapperCSSClass}`);
    this.shiveRenderService.appendChild(mainContentEl, SortableRowsDirective.dragHelperElement);

    this.rowDragStarted$.emit();
  }

  // Called as the element is dragged; update its translation.
  private dragMoveListener(event: Interact.DragEvent): void {
    if (!SortableRowsDirective.dragHelperElement) {
      return;
    }
    // Update the helper’s position as the mouse moves.
    this.setDragHelperPosition(
      this.touchAndMouseHandlerService.currentMouseViewportPos.x,
      event.clientY,
    );
  }

  // Called as soon as an overlap into a dropzone occurs.
  private onDragEnterEvent(event: Interact.DropEvent): void {
    let draggedIdx = SortableRowsDirective.draggedSortedIndex;
    const draggedRow = SortableRowsDirective.currentDraggedRow;
    const rowHeight = draggedRow.getBoundingClientRect().height;
    const target = event.target as HTMLElement;
    const container = target.parentNode as HTMLElement;

    const targetSortedIndex = this.sortableEntriesService.getSortedIndex(target);
    if (draggedIdx === targetSortedIndex) {
      // Nothing to do.
      return null;
    }

    const delta = this.sortableEntriesService.moveElementUpAndDown(
      target,
      container,
      draggedIdx,
      'tr',
    );

    if (delta === null) {
      return;
    }

    // Update both rows’ sorted-index attributes
    draggedIdx -= delta;
    this.sortableEntriesService.setSortedIndex(draggedRow, draggedIdx);
    SortableRowsDirective.draggedSortedIndex = draggedIdx;

    // Apply the transform & trigger animation
    this.sortableEntriesService.updateAndAnimateEntries(draggedRow, draggedIdx, -delta * rowHeight);
  }

  // When a dragged row is dropped on another row.
  private onDrop(): void {
    // Nothing to do here.
    if (!SortableRowsDirective.currentDraggedRow) {
      return;
    }

    const draggedItemId = Number(
      this.shiveRenderService.getAttribute(
        SortableRowsDirective.currentDraggedRow,
        SortableRowItemIdAttribute,
      ),
    );
    const fromIndex = this.clonedDataSource().findIndex((item) => item.id === draggedItemId);

    const toIndex = +this.shiveRenderService.getAttribute(
      SortableRowsDirective.currentDraggedRow,
      SortedIndexAttribute,
    );

    // Remove classes
    this.shiveRenderService.removeClass(
      SortableRowsDirective.currentDraggedRow,
      DragPlaceholderCSSClass,
    );
    this.shiveRenderService.removeClass(
      SortableRowsDirective.currentDraggedRow,
      DragActiveEntryCSSClass,
    );

    // Revert non-selection
    this.shiveRenderService.removeClass(document.body, 'noselect');
    this.shiveRenderService.removeClass(document.body, 'notouch');

    if (SortableRowsDirective.dragHelperElement) {
      this.shiveRenderService.removeChild(document.body, SortableRowsDirective.dragHelperElement);
      SortableRowsDirective.dragHelperElement = null;
    }

    // Reset
    this.shiveRenderService.addClass(SortableRowsDirective.currentDraggedRow, SortableRowCSSClass);
    SortableRowsDirective.currentDraggedRow = null;
    SortableRowsDirective.draggedSortedIndex = -1;
    interact.stop();

    // Reset CSS styles and position attribute.
    const allRows = Array.from(document.querySelectorAll(`tr.${SortableRowDropzoneCSSClass}`));
    allRows.forEach((row: HTMLTableRowElement) => {
      this.sortableEntriesService.setY(row, 0);
      const sortedIndex = +this.shiveRenderService.getAttribute(row, SortedIndexAttribute);

      // Reset animation properties -> Override transform to prevent strange flickering.
      this.shiveRenderService.setStyle(
        row,
        'transform',
        'translateY(0px)',
        RendererStyleFlags2.Important,
      );
      this.sortableEntriesService.updateAndAnimateEntries(row, sortedIndex, 0, true);
    });

    // Emit changed index positions
    this.rowDrop$.emit({ from: fromIndex, to: toIndex });
  }

  private initializeDragAndDrop(): void {
    // Make the element draggable.
    interact(this.el.nativeElement)
      .draggable({
        manualStart: true,
        inertia: true,
        listeners: {
          start: (event) => this.onDragStart(event),
          move: this.onDragMoveThrottled,
        },
      })
      // Also make it a dropzone.
      .dropzone({
        // Optionally, limit dropzones to rows with a specific class.
        accept: `.${CssDropzone}`,
        overlap: 'pointer',
        checker(dragEvent, event, dropped, dropzone, dropElement) {
          if (dropElement.classList.contains(DragPlaceholderCSSClass)) {
            return;
          }

          // Get dropzone's bounding rectangle.
          const dropRect = dropElement.getBoundingClientRect();
          return event.clientY >= dropRect.top && event.clientY <= dropRect.bottom;
        },
        ondragenter: this.onDragEnterThrottled,
      });
  }

  private setDragHelperPosition(xPos: number, yPos: number) {
    this.shiveRenderService.setStyle(
      SortableRowsDirective.dragHelperElement,
      CssAttributes.left,
      `${xPos}px`,
    );

    this.shiveRenderService.setStyle(
      SortableRowsDirective.dragHelperElement,
      CssAttributes.top,
      `${SortableRowsDirective.scrollPosY + yPos}px`,
    );

    this.shiveRenderService.setAttribute(
      SortableRowsDirective.dragHelperElement,
      'data-x',
      xPos.toString(),
    );

    this.shiveRenderService.setAttribute(
      SortableRowsDirective.dragHelperElement,
      'data-y',
      yPos.toString(),
    );
  }

  private createDragHelperElement(): void {
    // Create the helper element when dragging starts.
    SortableRowsDirective.dragHelperElement = this.shiveRenderService.createElement(
      'div',
    ) as HTMLElement;
    this.shiveRenderService.addClass(SortableRowsDirective.dragHelperElement, DragHelperCSSClass);

    // Set the content for the helper.
    SortableRowsDirective.dragHelperElement.innerHTML = this.dragHelperContent() || '';

    // Set initial styling and position.
    this.shiveRenderService.setStyle(
      SortableRowsDirective.dragHelperElement,
      CssAttributes.position,
      'absolute',
    );
  }
}
